
    import {Inject, Options, Prop, Vue, Watch} from 'vue-property-decorator';
    import {escapeHtml} from '@malt/sanitize';
    import {Getter} from 'vuex-class';
    import {addDays, addMonths, addWeeks, differenceInWeeks} from 'date-fns';
    import {context} from 'vue3-starter/src/typescript/app-context/AppContext';
    import TooltipInfo from '@/brief-hub/common/ui/components/TooltipInfo.vue';
    import UserResource from '@/brief-hub/user/UserResource';
    import type {BriefFreelancerProfile} from '@/brief-hub/store/modules/brief/types';
    import type {ConditionsWhenConstraints} from '@/brief-hub/deposit/conditions/ConditionsWhenConstraints';
    import type {CategoryResult} from '@/brief-hub/deposit/freelancer/CategoryResult';
    import {BriefApiExperienceLevel, BriefResource, DurationUnit, FreelancerAvailability} from '@/common/api/api-raw-types';
    import {CompanyFeesService} from '@/brief-hub/brief/CompanyFeesService';
    import {DI} from '@/brief-hub/config/DependencyInjection';
    import {WORKED_DAYS_MAPPING} from '@/common/config/variables';

    interface estimatedBudget {
        freelancerQuote: number;
        fees: number;
        total: number;
    }

    @Options({
        components: {
            TooltipInfo,
        },
    })
    export default class BudgetTooltip extends Vue {
        @Prop({type: Number, default: null})
        tjmRecommendation!: number | null;

        @Prop({type: Number})
        dailyFreelancerRate!: number;

        @Prop({type: String})
        durationEstimation!: string;

        @Prop({type: Number})
        duration!: number;

        @Prop({type: String})
        durationUnit!: DurationUnit;

        @Prop({type: Date})
        startDate!: Date;

        @Prop({type: String})
        availability!: FreelancerAvailability | null;

        @Prop({type: Number})
        budget!: number;

        @Prop({type: Object})
        constraints!: ConditionsWhenConstraints;

        @Getter('briefFreelancerProfile')
        briefFreelancerProfile!: BriefFreelancerProfile;

        @Getter('connectedUser')
        user!: UserResource | null;

        @Getter('brief')
        brief!: BriefResource | null;

        @Inject({from: DI.companyFeesService})
        companyFeesService!: CompanyFeesService;

        private category: CategoryResult | null = null;
        private experienceLevel: BriefApiExperienceLevel | null = null;
        private companyFees: number | null = null;

        created(): void {
            this.category = {
                name: this.briefFreelancerProfile.selectedCategoryLabel,
                category: this.briefFreelancerProfile.selectedCategory,
            };
            this.experienceLevel = this.briefFreelancerProfile.selectedExperienceLevel;
            this.fetchCompanyFees();
        }

        get tjmRecommendationLabel(): string | null {
            if (this.category && this.experienceLevel) {
                return this.$msg(
                    'customer.brief.form.when.conditions.tooltip.budget.tjm.recommendation',
                    escapeHtml(this.category.name),
                    this.$msg(`customer.brief.form.freelancer.profile.level.${escapeHtml(this.experienceLevel)}`),
                );
            }

            return null;
        }

        get estimatedBudget(): estimatedBudget {
            let freelancerQuote = 0;

            if (
                this.durationEstimation === 'SUPERIOR_ONE_MONTH' &&
                this.duration &&
                this.duration >= this.constraints.MIN_DURATION &&
                this.duration <= this.constraints.MAX_DURATION &&
                this.dailyFreelancerRate &&
                this.dailyFreelancerRate >= this.constraints.MIN_FREELANCER_RATE &&
                this.dailyFreelancerRate <= this.constraints.MAX_FREELANCER_RATE &&
                this.availability &&
                this.startDate
            ) {
                let endDate;
                switch (this.durationUnit) {
                    case DurationUnit.DAYS:
                        endDate = addDays(this.startDate, this.duration);
                        break;
                    case DurationUnit.WEEKS:
                        endDate = addWeeks(this.startDate, this.duration);
                        break;
                    case DurationUnit.MONTHS:
                        endDate = addMonths(this.startDate, this.duration);
                        break;
                }
                const nbWeeks = differenceInWeeks(endDate, this.startDate);
                const workedDaysPerWeek = WORKED_DAYS_MAPPING[this.availability];

                freelancerQuote = this.dailyFreelancerRate * workedDaysPerWeek * nbWeeks;
            } else if (
                this.durationEstimation === 'INFERIOR_ONE_MONTH' &&
                this.budget &&
                this.budget >= this.constraints.MIN_BUDGET &&
                this.budget <= this.constraints.MAX_BUDGET
            ) {
                freelancerQuote = this.budget;
            }

            const fees = this.companyFees != null ? (this.companyFees / 100) * freelancerQuote : 0;

            return {
                freelancerQuote,
                fees,
                total: freelancerQuote + fees,
            };
        }

        get contractLabel(): string {
            return this.user && this.companyFees
                ? this.$msg('customer.brief.form.when.conditions.tooltip.budget.contract.v2')
                : this.$msg('customer.brief.form.when.conditions.tooltip.budget.contract.unlogged');
        }

        formattedCurrency(value: number): string {
            return Intl.NumberFormat(context.locale, {style: 'currency', currency: this.brief?.briefDetails?.currencyCode}).format(value);
        }

        @Watch('user')
        updateFees(): void {
            this.fetchCompanyFees();
        }

        private async fetchCompanyFees(): Promise<void> {
            if (this.user) {
                const [fees, error] = await this.companyFeesService.fetchUserFees(this.user);
                if (fees && !error) {
                    this.companyFees = fees.regularFees;
                }
            } else {
                const [fees, error] = await this.companyFeesService.fetchDefaultFees();
                if (fees && !error) {
                    this.companyFees = fees.regularFees;
                }
            }
        }
    }
